import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  ChipOption,
  ChipSelectorComponent,
} from '../chip-selector/chip-selector.component';
import {
  ActionButtonColumnDefinition,
  ActionMenuColumnDefinition,
  ActionTooltip,
  ColumnDefinition,
  ColumnFilterConfig,
  DataColumnDefinition,
  GroupByConfig,
  PaginatorConfig,
  QTableData,
  TableAction,
} from './column-definition.interface';
import { matchesSearch, toSearchTerms } from 'src/app/utils/text-search';

@Component({
  selector: 'qs-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ChipSelectorComponent,
    MatTooltipModule,
    MatPaginatorModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './qs-table.component.html',
  styleUrls: ['./qs-table.component.scss'],
})
export class QsTableComponent<T extends Record<string, any>>
  implements OnChanges, AfterViewInit
{
  private cdk = inject(ChangeDetectorRef);
  readonly paginatorIntl = inject(MatPaginatorIntl);

  @Input() data: QTableData<T>[] = [];
  @Input() columns: ColumnDefinition<QTableData<T>>[] = [];
  @Input() expandable: boolean = false;
  @Input() canExpandRow: (row: QTableData<T>) => boolean = () => true;
  @Input() onRowClickFn?: (row: QTableData<T>) => void;

  @Input() detailTemplate?: TemplateRef<any>;
  @Input() isSearchEnabled: boolean = false;
  @Input() columnFilterConfig?: ColumnFilterConfig;
  @Input() groupByConfig?: GroupByConfig;
  @Input() isPaginatorEnabled: boolean = false;
  /**
   * Stable hook for end-to-end tests. When a caller names its table, the rendered table, every
   * data row and every cell get a derived data-testid, so specs address them without relying on
   * translated headers or on Material's internal markup.
   */
  @Input() testId: string = '';
  /**
   * What identifies a single row in its test ids, so a spec can address one row instead of the
   * whole column. Rows and cells keep their table-level test ids when a caller names none, since a
   * row's position is not stable across sorting, filtering and paging.
   */
  @Input() rowTestIdFn?: (row: QTableData<T>) => string;
  @Input() rowStyleFn?: (row: QTableData<T>) => { [key: string]: string };
  @Input() paginatorConfig: PaginatorConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  };

  selectedPageSize: number | 'all' = 10;

  isGroupByEnabled: boolean = false;
  // Keep a copy of the original data for filtering
  originalData: QTableData<T>[] = [];
  dataSource = new MatTableDataSource<QTableData<T>>(this.data);
  private activeColumnFilterField = '';
  private selectedColumnFilterValues: string[] = [];

  expandedElement: QTableData<T> | null = null;
  searchText: string = '';

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('groupByCheckbox') groupByCheckbox!: ElementRef<HTMLInputElement>;

  get pageSizeOptions(): number[] {
    return [
      ...new Set([
        ...this.paginatorConfig.pageSizeOptions,
        this.paginatorConfig.pageSize,
        10,
        100,
      ]),
    ]
      .filter((pageSize) => pageSize > 0)
      .sort((a, b) => a - b);
  }

  get effectivePageSize(): number {
    return this.selectedPageSize === 'all'
      ? Math.max(this.dataSource.filteredData.length, 1)
      : this.selectedPageSize;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    if (this.isPaginatorEnabled) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort)
      this.sort.sortChange.subscribe(() => {
        this.clearGroupBy();
      });
    if (this.groupByConfig?.field)
      this.data.forEach((row: any) => {
        row.isFirstInGroup = false;
        row.isLastInGroup = false;
        return row;
      });
    this.dataSource.sortData = this.customSort.bind(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paginatorConfig']) {
      const configuredPageSize = this.paginatorConfig.pageSize;
      this.selectedPageSize =
        Number.isFinite(configuredPageSize) && configuredPageSize > 0
          ? configuredPageSize
          : 10;
    }

    if (changes['data']) {
      this.originalData = this.data;
      this.searchText =
        this.searchInput?.nativeElement.value ?? this.searchText;
      this.applyActiveFilters();
    } else if (changes['paginatorConfig']) {
      this.resetPaginator();
    }
  }

  onPageSizeChange(pageSize: number | 'all'): void {
    this.selectedPageSize = pageSize;
    this.resetPaginator();
  }
  onSearchInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.applyFilter(inputElement.value);
  }
  clearAllFilterAndSort(): void {
    if (this.isSearchEnabled) {
      this.searchText = '';
      this.searchInput.nativeElement.value = '';
    }
    this.activeColumnFilterField = '';
    this.selectedColumnFilterValues = [];
    if (this.columnFilterConfig) {
      this.columnFilterConfig.filterOptions =
        this.columnFilterConfig.filterOptions.map((option) => ({
          ...option,
          isSelected: false,
        }));
    }
    this.clearSort();
    this.dataSource.data = this.originalData;
    this.resetPaginator();
  }

  onClickGroupBy(): void {
    this.clearAllFilterAndSort();
    this.isGroupByEnabled = !this.isGroupByEnabled;
    if (this.isGroupByEnabled) {
      this.groupTableData();
    }
  }

  clearGroupBy(): void {
    if (this.isGroupByEnabled) {
      this.isGroupByEnabled = false;
      if (this.groupByCheckbox) {
        this.groupByCheckbox.nativeElement.checked = false;
      }
    }
  }

  groupTableData(): void {
    let sortedData = [...this.originalData];

    if (this.groupByConfig?.field) {
      if (this.groupByConfig.sortFn) {
        sortedData.sort(this.groupByConfig.sortFn);
      } else {
        sortedData.sort((a, b) => {
          if (a[this.groupByConfig?.field!] < b[this.groupByConfig?.field!])
            return -1;
          if (a[this.groupByConfig?.field!] > b[this.groupByConfig?.field!])
            return 1;
          return 0;
        });
      }
    }
    if (this.groupByConfig?.format) {
      sortedData.forEach((row, index) => {
        row.isFirstInGroup =
          index === 0 ||
          this.groupByConfig?.format!(sortedData[index - 1]) !==
            this.groupByConfig?.format!(row);
        row.isLastInGroup =
          index === sortedData.length - 1 ||
          this.groupByConfig?.format!(sortedData[index + 1]) !==
            this.groupByConfig?.format!(row);
      });
    } else {
      sortedData.forEach((row, index) => {
        row.isFirstInGroup =
          index === 0 ||
          sortedData[index - 1][this.groupByConfig?.field!] !==
            row[this.groupByConfig?.field!];
        row.isLastInGroup =
          index === sortedData.length - 1 ||
          sortedData[index + 1][this.groupByConfig?.field!] !==
            row[this.groupByConfig?.field!];
      });
    }

    this.dataSource.data = sortedData;
  }

  clearSort(): void {
    if (this.sort) {
      this.sort.active = '';
      this.sort.direction = '';
    }
  }

  applyFilter(searchValue: string) {
    this.searchText = searchValue;
    this.clearGroupBy();
    this.applyActiveFilters();
  }

  private rowMatchesSearch(row: QTableData<T>): boolean {
    const displayedValues = this.columns
      .filter((column) => column.type === 'data')
      .map((column) => this.getDataCellDisplayValue(row, column));

    const rawValues: string[] = [];
    this.collectSearchableValues(row, rawValues, new Set<object>());

    return matchesSearch(this.searchText, [...displayedValues, ...rawValues]);
  }

  private collectSearchableValues(
    value: unknown,
    values: string[],
    visited: Set<object>,
  ): void {
    if (value === null || value === undefined) {
      return;
    }

    if (typeof value !== 'object') {
      values.push(String(value));
      return;
    }

    if (value instanceof Date) {
      values.push(this.formatDate(value));
      return;
    }

    if (visited.has(value)) {
      return;
    }

    visited.add(value);
    Object.values(value).forEach((nestedValue) =>
      this.collectSearchableValues(nestedValue, values, visited),
    );
  }

  applyColumnFilter(selectedChips: ChipOption[], field: string) {
    this.clearGroupBy();
    this.activeColumnFilterField = field;
    this.selectedColumnFilterValues = selectedChips
      .filter((opt) => opt.isSelected)
      .map((opt) => opt.value);
    this.applyActiveFilters();
    this.cdk.detectChanges();
  }

  private applyActiveFilters(): void {
    let filteredRows = this.originalData;

    if (this.selectedColumnFilterValues.length > 0) {
      filteredRows = this.columnFilterConfig?.filterFunc
        ? this.columnFilterConfig.filterFunc(
            filteredRows,
            this.selectedColumnFilterValues,
          )
        : filteredRows.filter((row) =>
            this.selectedColumnFilterValues.includes(
              String(this.getNestedValue(row, this.activeColumnFilterField)),
            ),
          );
    }

    if (toSearchTerms(this.searchText).length > 0) {
      filteredRows = filteredRows.filter((row) => this.rowMatchesSearch(row));
    }

    this.dataSource.data = filteredRows;
    this.resetPaginator();
  }

  private resetPaginator(): void {
    if (!this.paginator || !this.isPaginatorEnabled) {
      return;
    }

    const previousPageIndex = this.paginator.pageIndex;
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = this.effectivePageSize;
    this.paginator.page.emit({
      previousPageIndex,
      pageIndex: 0,
      pageSize: this.effectivePageSize,
      length: this.paginator.length,
    } satisfies PageEvent);
  }

  // Toggle the expanded state for a row
  toggleRow(row: QTableData<T>): void {
    if (!this.canExpand(row)) {
      return;
    }
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  canExpand(row: QTableData<T>): boolean {
    return !this.expandable || this.canExpandRow(row);
  }

  getColumnDef(col: ColumnDefinition<QTableData<T>>): string {
    return col.type === 'data' ? col.field : col.header || 'action';
  }

  /**
   * `<testId>-row-<rowId>` when the caller names its rows, `<testId>-row` when it names only the
   * table, and nothing when it names neither.
   */
  getRowTestId(row: QTableData<T>): string | null {
    if (!this.testId) {
      return null;
    }
    return this.withRowSuffix(`${this.testId}-row`, row);
  }

  /**
   * `<testId>-cell-<column>-<rowId>` when the caller names its rows, `<testId>-cell-<column>` when
   * it names only the table, and nothing when it names neither.
   */
  getCellTestId(
    col: ColumnDefinition<QTableData<T>>,
    row: QTableData<T>,
  ): string | null {
    if (!this.testId) {
      return null;
    }
    const column = this.getColumnTestIdPart(col);
    return this.withRowSuffix(`${this.testId}-cell-${column}`, row);
  }

  // A test id names one row only where the caller gave rows an identifier of their own. Without
  // one the id stays at table level, which still addresses a whole row set or a whole column.
  private withRowSuffix(testId: string, row: QTableData<T>): string {
    const rowId = this.rowTestIdFn?.(row);
    return rowId ? `${testId}-${rowId}` : testId;
  }

  // The column's own name, its field for data columns, and a slug of the header for action
  // columns, whose header is free display text rather than a key.
  private getColumnTestIdPart(col: ColumnDefinition<QTableData<T>>): string {
    if (col.testId) {
      return col.testId;
    }
    if (col.type === 'data') {
      return col.field;
    }
    return this.slugify(col.header || '') || 'action';
  }

  // Lowercase ASCII words joined by hyphens, so translated text cannot malform a test id.
  private slugify(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .toLowerCase()
      .replace(/^-+|-+$/g, '');
  }

  get columnKeysWithExpand(): string[] {
    return this.expandable ? [...this.columnKeys, 'expand'] : this.columnKeys;
  }
  // Compute column keys for the MatTable.
  // For data columns, we use the field; for action columns, we fallback to header or 'action'
  get columnKeys(): string[] {
    return this.columns.map((col) => {
      if (col.type === 'data') {
        return col.field;
      }
      return col.header || 'action';
    });
  }

  // Custom sort function that uses a column's custom sort function if provided.
  customSort(data: QTableData<T>[], sort: MatSort): QTableData<T>[] {
    const active = sort.active;
    const direction = sort.direction;
    if (!active || direction === '') {
      return data;
    }
    // Find the matching data column for the active sort header
    const colDef = this.columns.find(
      (col) => col.type === 'data' && col.field === active,
    ) as DataColumnDefinition<QTableData<T>>;

    const multiplier = direction === 'asc' ? 1 : -1;
    if (colDef && colDef.sortFn) {
      // We use the non-null assertion (!) since we know sortFn exists
      return data.sort((a, b) => colDef.sortFn!(a, b, multiplier));
    } else {
      // Default sort if no custom sort function is provided
      return data.sort((a, b) => {
        const valueA = a[active];
        const valueB = b[active];
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      });
    }
  }

  // Handle clicks on data cells if an onClick callback is defined.
  handleDataCellClick(
    row: QTableData<T>,
    col: ColumnDefinition<QTableData<T>>,
  ) {
    if (col.type === 'data') {
      (col as DataColumnDefinition<QTableData<T>>).onClick?.(row);
    }
  }

  getDataCellDisplayValue(
    row: QTableData<T>,
    col: ColumnDefinition<QTableData<T>>,
  ): string {
    if (col.type !== 'data') {
      return '';
    }

    const value = col.format
      ? col.format(row)
      : this.getNestedValue(row, col.field);
    if (value === null || value === undefined) {
      return '';
    }

    if (!col.format) {
      const formattedDateTime = this.formatDateTimeValue(value);
      if (formattedDateTime) {
        return formattedDateTime;
      }
    }

    return String(value);
  }

  getDataCellTooltip(
    row: QTableData<T>,
    col: ColumnDefinition<QTableData<T>>,
  ): string {
    if (col.type !== 'data') {
      return '';
    }

    return (
      this.resolveActionTooltip(col.tooltip, row) ||
      this.getDataCellDisplayValue(row, col)
    );
  }

  private formatDateTimeValue(value: unknown): string {
    if (value instanceof Date) {
      return this.formatDate(value);
    }

    if (typeof value !== 'string') {
      return '';
    }

    const trimmedValue = value.trim();
    if (!this.isIsoDateTimeValue(trimmedValue)) {
      return '';
    }

    const date = new Date(trimmedValue);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return this.formatDate(date);
  }

  private isIsoDateTimeValue(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/.test(
      value,
    );
  }

  private formatDate(date: Date): string {
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  }

  getNestedValue(row: any, field: string): any {
    return field.split('.').reduce((obj, key) => obj?.[key], row);
  }

  isActionButtonDisabled(
    col: ActionButtonColumnDefinition<QTableData<T>>,
    row: QTableData<T>,
  ): boolean {
    return col.isDisabled ? col.isDisabled(row) : false;
  }

  getActionButtonTooltip(
    col: ActionButtonColumnDefinition<QTableData<T>>,
    row: QTableData<T>,
  ): string {
    return this.resolveActionTooltip(col.tooltip, row) || col.buttonName;
  }

  isTableActionDisabled(
    action: TableAction<QTableData<T>>,
    row: QTableData<T>,
  ): boolean {
    return action.isDisabled ? action.isDisabled(row) : false;
  }

  getTableActionTooltip(
    action: TableAction<QTableData<T>>,
    row: QTableData<T>,
  ): string {
    return this.resolveActionTooltip(action.tooltip, row);
  }

  handleTableActionClick(
    event: Event,
    action: TableAction<QTableData<T>>,
    row: QTableData<T>,
  ): void {
    event.stopPropagation();

    if (this.isTableActionDisabled(action, row)) {
      return;
    }

    action.callback(row);
  }

  asActionButtonColumn(
    col: ColumnDefinition<QTableData<T>>,
  ): ActionButtonColumnDefinition<QTableData<T>> {
    return col as ActionButtonColumnDefinition<QTableData<T>>;
  }

  asActionMenuColumn(
    col: ColumnDefinition<QTableData<T>>,
  ): ActionMenuColumnDefinition<QTableData<T>> {
    return col as ActionMenuColumnDefinition<QTableData<T>>;
  }

  private resolveActionTooltip(
    tooltip: ActionTooltip<QTableData<T>> | undefined,
    row: QTableData<T>,
  ): string {
    if (!tooltip) {
      return '';
    }

    return typeof tooltip === 'function' ? tooltip(row) : tooltip;
  }

  getHeaderCellStyle(
    col: ColumnDefinition<QTableData<T>>,
  ): Record<string, string> {
    return this.getColumnWidthStyle(col);
  }

  getCellStyle(
    row: QTableData<T>,
    col: ColumnDefinition<QTableData<T>>,
  ): Record<string, string> {
    return {
      ...(col.style ? col.style(row) : {}),
      ...this.getColumnWidthStyle(col),
    };
  }

  private getColumnWidthStyle(
    col: ColumnDefinition<QTableData<T>>,
  ): Record<string, string> {
    const width = col.width || (col.type === 'action' ? '72px' : '');
    return width ? { width, 'max-width': width } : {};
  }
}
